<template>
  <div :class="[visibleNotifications.length > 0 && $style.notificationBarWrapper]">
    <div
      v-if="visibleNotifications.length > 0"
      :class="[$style.notificationBar, {[$style.notificationBarMore]: visibleNotifications.length > 1}]"
    >
      <aw-spring :desktop-size="12">
        <div :class="$style.notificationBarCarousel">
          <client-only>
            <b-carousel
              ref="notificationCarouselRef"
              v-model="slideNavigation"
              :interval="4200"
              ride="carousel"
              :indicators="visibleNotifications.length > 1"
              class="carousel-notification-bar"
              @keyup="onPress"
            >
              <b-carousel-slide
                v-for="notification in visibleNotificationItems"
                :key="notification.id"
                background="transparent"
                :class="$style.notificationCarouselInner"
              >
                <template #img>
                  <div :class="$style.notificationBarItemWrapper">
                    <div
                      :class="$style.notificationBarItem"
                      v-html="notification.content"
                    />
                  </div>
                </template>
              </b-carousel-slide>
            </b-carousel>
          </client-only>
        </div>
        <div
          :class="[
            $style.notificationBarBackground,
            {
              [$style.notificationBarBackgroundOneItem]: visibleNotifications.length === 1,
            },
          ]"
        />
        <template v-if="visibleNotifications.length > 1">
          <lv-button styling="reset" :class="$style.notificationBarButton" @click="toggleAutoPlay">
            <lv-icon
              :size="16"
              :name="(carosuelAutoPlay
                ? 'pause-version2-16'
                : 'play-version2-16'
              )"
            />
          </lv-button>
        </template>
        <div
          v-if="visibleNotifications.length > 0"
          :class="$style.hiddenNotificationBar"
          :aria-hidden="true"
        >
          <div
            v-for="notification in visibleNotifications"
            :key="notification.id"
            :class="[
              $style.notificationBarItemWrapper,
              $style.hiddenNotificationBarItemWrapper,
            ]"
          >
            <div
              :class="$style.notificationBarItem"
              v-html="notification.content"
            />
          </div>
        </div>
      </aw-spring>
    </div>
  </div>
</template>

<script setup>
  import { computed, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue';
  import { useNuxtApp, useRouter } from 'nuxt/app';
  import AwSpring from '~~/common/components/AwSpring';
  import { LvButton, LvIcon } from '~~/common/components/loginet-vue-shop/index';
  import { CT_FOOD } from '~~/common/config/CheckoutTypes';
  import { notificationTokenizer } from '~~/common/utils/helper';
  import { useNotificationStore } from '~~/common/stores/notification.js';
  import { useOrderStore } from '~~/common/stores/order';
  import { useNotificationBarStore } from '~~/shop/app/stores/notificationBar.js';
  import { DEALER_ACTUAL_MESSAGE_TYPE_HEADER } from '~~/dealer/app/config/DealerActualMessage';
  import { useDealerNotificationStore } from '~~/dealer/app/stores/dealerNotification';

  const {
    $awt,
    $cookies,
  } = useNuxtApp();

  const router = useRouter();

  const componentId = useId();
  const cbKey = `awnotificationbar_${componentId}`;

  const notificationStore = useNotificationStore();
  const notificationBarStore = useNotificationBarStore();
  const dealerNotificationStore = useDealerNotificationStore();
  const orderStore = useOrderStore();

  const notificationInfoFromCookie = $cookies.get('aw_notification_info');
  const now = ref(Date.now());
  const notificationsFromNotificationBarStore = computed(() => {
    const storeData = notificationBarStore.notifications?.notifications;
    const arrayData = Array.isArray(storeData) ? storeData : [];
    return Object.fromEntries(arrayData.map(x => [x.id, {
      ...x,
      content: x.content.replace(
        /awCloseNotificationBar/g,
        `javascript:window['${cbKey}'](${x.id});`,
      ),
    }]));
  });

  if (import.meta.server) {
    const notificationInfos = {};
    for (const notificationId in notificationsFromNotificationBarStore.value) {
      const cookieNoti = notificationInfoFromCookie?.[notificationId];
      notificationInfos[notificationId] = {
        view_count: (cookieNoti?.view_count ?? 0) + 1,
        force_hidden: cookieNoti?.force_hidden ?? false,
      };
    }
    $cookies.set('aw_notification_info', notificationInfos, {
      path: '/',
      sameSite: 'lax',
    });
  }

  const notifications = computed(() => {

    let shopNotifications = [];
    if (__AW_IS_SHOP__) {
      const FIXED_ID_LOYALTY = 'loyalty-program-next-level';
      const FIXED_ID_FREE_FOOD = 'free-food-delivery';
      const FIXED_ID_LOYALTY_COUPON_PRODUCT_DISCOUNT = 'loyalty-coupon-product-discount';
      const orderResult = {
        [FIXED_ID_LOYALTY]: {
          id: FIXED_ID_LOYALTY,
          content: orderStore.order?.infos?.loyaltyCardNextLevelInfo,
          whitelist: true,
          urlList: [],
        },
        [FIXED_ID_FREE_FOOD]: {
          id: FIXED_ID_FREE_FOOD,
          content: orderStore.getRawStaticInfo?.[CT_FOOD]?.freeDeliveryInfo,
          whitelist: true,
          urlList: [],
        },
        [FIXED_ID_LOYALTY_COUPON_PRODUCT_DISCOUNT]: {
          id: FIXED_ID_LOYALTY_COUPON_PRODUCT_DISCOUNT,
          content: orderStore.order?.infos?.loyaltyCouponProductDiscount,
          whitelist: true,
          urlList: [],
        },
      };
      const notificationsOrder = notificationBarStore.notifications?.priority || [];
      shopNotifications = (notificationsOrder
        .map((id) => {
          return (
            (orderResult[id]?.content
              ? orderResult[id]
              : null
            )
            || notificationsFromNotificationBarStore.value[id]
          );
        })
        .filter((notification) => {
          if (!notification) {
            return false;
          }

          if (orderResult[notification.id]) {
            return true;
          }

          if (notificationInfoFromCookie?.[notification.id]?.force_hidden) {
            notification.hidden = true;
            return false;
          }

          if (notification.appearanceNumber !== 0 && notificationInfoFromCookie?.[notification.id]?.view_count > notification.appearanceNumber) {
            notification.hidden = true;
            return false;
          }

          if (new Date(notification.viewToDate) < now.value) {
            notification.hidden = true;
            return false;
          }

          if (notification.urlList.length === 0) {
            return true;
          }

          if (notification.whitelist) {
            return notification.urlList.some((url) => router.currentRoute.value.fullPath.startsWith(url));
          } else {
            return notification.urlList.every((url) => !router.currentRoute.value.fullPath.startsWith(url));
          }
        })
      );
    } else {
      const notificationsFromStore = dealerNotificationStore.notifications;
      shopNotifications = (notificationsFromStore
        .filter(n => n.type === DEALER_ACTUAL_MESSAGE_TYPE_HEADER)
        .map((n) => {
          return { content: `<strong>${n.title}</strong> ${n.message}` };
        })
      );
    }

    const infoOrWarningNotifications = Object.values(notificationStore.notifications)?.map((notification) => {
      const noti = notificationTokenizer({ text: notification?.text }, { awt: $awt });
      return noti.title + ' ' + noti.subtitle;
    });

    const result = [...infoOrWarningNotifications, ...shopNotifications];
    // slice the LAST 4 items
    return result.length > 4 ? result.slice(result.length - 4, result.length) : result;
  });

  const visibleNotifications = computed(() => notifications.value.filter(notification => !notification.hidden));

  // if visibleNotifications. length === 1, then show this one item twice, otherwise, show all items.
  const visibleNotificationItems = computed(() => visibleNotifications.value.length === 1 ? [visibleNotifications.value[0], visibleNotifications.value[0]] : visibleNotifications.value);

  const notificationCarouselRef = ref(null);
  const slideNavigation = ref(0);
  const carosuelAutoPlay = ref(true);

  const pause = () => notificationCarouselRef.value?.pause();
  const resume = () => notificationCarouselRef.value?.resume();
  const next = () => notificationCarouselRef.value?.next();
  const prev = () => notificationCarouselRef.value?.prev();

  function toggleAutoPlay () {
    if (carosuelAutoPlay.value) {
      pause();
    } else {
      resume();
    }
    carosuelAutoPlay.value = !carosuelAutoPlay.value;
  }

  function onPress (event) {
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        prev();
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        next();
        break;
      default:
        break;
    }
  }

  function hideNotification (id) {
    notificationInfoFromCookie[id].force_hidden = true;
    $cookies.set('aw_notification_info', notificationInfoFromCookie, {
      path: '/',
      sameSite: 'lax',
    });
  }

  watch(
    visibleNotifications,
    (newValue, oldValue) => {
      if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
        slideNavigation.value = 0;
      }
    },
    {
      deep: true,
      immediate: true,
    },
  );
  const timer = ref(null);

  onMounted(() => {
    window[cbKey] = (id) => {
      hideNotification(id);
    };
    timer.value = setInterval(() => {
      now.value = Date.now();
    }, 1000);
  });

  onBeforeUnmount(() => {
    clearInterval(timer.value);
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete window[cbKey];
  });

</script>


<style module lang="scss" rel="stylesheet/scss">
.notificationBar {
  font-family: $secondary-font;
  z-index: 100;
  width: 100%;

  [data-spring-container] {
    display: grid;
    grid-template-columns: auto;
    gap: 4px;
    align-items: center;

  }

  &More {
    [data-spring-container] {
      grid-template-columns: auto 24px;
    }
  }

  &Carousel {
    position: relative;
    align-self: stretch;
  }

  &Item {
    --allowedLines: 3;
    @include tablet(min) {
      --allowedLines: 2;
    }
    @include desktop-small(min) {
      --allowedLines: 1;
    }
    @include multiline-ellipsis(var(--allowedLines));
    font-size: 10px;
    font-weight: $font-weight-normal-v2;
    line-height: 16px;
    text-align: left;

    @include tablet(min) {
      font-size: 12px;
      text-align: center;
    }

    &Wrapper {
      display: flex;
      overflow: hidden;
      align-items: center;
      justify-content: center;
      height: 100%;
      padding: 8px 12px 8px 31px;

      @include tablet(min) {
        padding: 8px 20px 8px 39px;
      }
    }

    a {
      text-decoration: underline;
    }
  }

  &Background {
    display: flex;
    grid-area: 1 / 1 / 1 / 1;
    align-self: stretch;
    padding: 8px 0 8px 19px;

    &OneItem {
      padding-left: 0;
    }

    &::before {
      flex-grow: 1;
      content: "";
      border-radius: 12px;
      background: $color-black-32;
    }
  }

  &Button {
    display: flex;
    align-items: center;
    width: 24px;
    height: 32px;
    padding: 8px 4px;
    border-radius: 12px;
    background: $color-black-32;

    &:focus,
    &:hover,
    &:active {
      background: $color-black-32;
    }
  }

  &Wrapper {
    min-height: 48px;
    color: $color-text-inverse;
    background: $color-sage;
  }
}

.notificationBarCarousel,
.hiddenNotificationBar {
  overflow: hidden;
  grid-area: 1 / 1 / 1 / 1;
  margin: 8px 0;
}

.hiddenNotificationBar {
  display: grid;
  visibility: hidden;

  &ItemWrapper {
    grid-area: 1 / 1 / 1 / 1;
  }
}

.notificationCarouselInner {
  // when a carousel item is deleted bootstrap does not add carousel-inner
  // class until the carousel rotates, so we hardcode this style here.
  height: 100%;
}
</style>
